import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Sparkle, CheckCircle, Spinner, Package, Copy, Lightning, Rocket, Browser, Info, ArrowRight, House, PencilSimple, ArrowDown } from '@phosphor-icons/react';
import DOMPurify from 'dompurify';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { downloadBlob as safeDownloadBlob } from '@/utils/downloadBlob';
import { getUser } from '@/utils/auth';
import { useWorkflowStore } from '@/store/workflowStore';
import OutputActions from './OutputActions';
import BuildYourOffer from './BuildYourOffer';
import StyledHtmlPreview from './StyledHtmlPreview';
import EditablePreview from './EditablePreview';
import WhyThisFitsYouBlock from './WhyThisFitsYouBlock';
import { useMissionHandoff } from '@/store/missionHandoffContext';

const BuildOfferModal = ({ isOpen, onClose, onSuccess, onAction, chatContext }) => {
  const user = getUser();
  const { handoff: missionHandoff } = useMissionHandoff();
  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [loading, setLoading] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  
  // Zustand workflow store
  const setOfferData = useWorkflowStore((state) => state.setOfferData);
  
  const [formData, setFormData] = useState({
    audience_pain_point: '',
    solution_description: '',
    offer_type: 'digital_product',
    price_range: 'mid'
  });

  // Load context when modal opens — chatbot suggestion takes priority
  useEffect(() => {
    if (isOpen && step === 'input') {
      if (chatContext) {
        prefillFromChatContext(chatContext);
      } else if (missionHandoff && missionHandoff.asset_id) {
        // Round 108 — mission handoff wins over Business Memory
        // fallback. The customer's approved edits become the seed.
        const at = missionHandoff.approved_edits || {};
        const so = missionHandoff.strategist_outputs || {};
        const audience = missionHandoff.target_audience || at.recommended_audience || '';
        const solution = at.pitch || at.headline || at.title || so.strategy || at.summary || '';
        setFormData(prev => ({
          ...prev,
          audience_pain_point: audience
            ? `My audience (${String(audience).slice(0, 150)}) needs help with`
            : prev.audience_pain_point,
          solution_description: solution
            ? String(solution).slice(0, 500)
            : prev.solution_description,
        }));
      } else {
        loadBusinessContext();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, chatContext, missionHandoff]);

  const prefillFromChatContext = (text) => {
    // Try parsing as structured JSON (from MonetizationPlanModal)
    try {
      const data = JSON.parse(text);
      setFormData(prev => ({
        ...prev,
        audience_pain_point: data.audience ? `My audience (${data.audience.slice(0, 150)}) needs help with` : prev.audience_pain_point,
        solution_description: data.solution || data.offer_name || prev.solution_description,
        offer_type: prev.offer_type,
      }));
      return;
    } catch {
      // Not JSON — it's raw chatbot text
    }
    // Extract chatbot suggestion into solution field
    setFormData(prev => ({
      ...prev,
      solution_description: text.slice(0, 500),
    }));
  };

  const loadBusinessContext = async () => {
    setContextLoading(true);
    try {
      const response = await api.get('/monetization/offer-context');
      const context = response.data;
      
      if (context.has_business_plan) {
        setFormData(prev => ({
          ...prev,
          audience_pain_point: context.audience_pain_point || prev.audience_pain_point,
          solution_description: context.solution_description || prev.solution_description,
          offer_type: context.offer_type || prev.offer_type
        }));
      }
    } catch (error) {
      // Silent — user can fill manually
    } finally {
      setContextLoading(false);
    }
  };

  const offerTypes = [
    { id: 'product', label: '📦 Product', desc: 'Physical goods, gift sets' },
    { id: 'digital_product', label: 'Digital Product', desc: 'eBooks, guides, templates' },
    { id: 'course', label: 'Online Course', desc: 'Video lessons, modules' },
    { id: 'coaching', label: 'Coaching', desc: '1-on-1 or group coaching' },
    { id: 'service', label: 'Service', desc: 'Done-for-you work' },
    { id: 'template', label: 'Templates/Tools', desc: 'Ready-to-use resources' }
  ];

  const priceRanges = [
    { id: 'low', label: '$10-50', desc: 'Entry-level' },
    { id: 'mid', label: '$50-200', desc: 'Standard' },
    { id: 'high', label: '$200+', desc: 'Premium' }
  ];

  const handleSubmit = async () => {
    if (!formData.audience_pain_point.trim() || !formData.solution_description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setStep('generating');
    setLoading(true);

    try {
      const response = await api.post('/monetization/build-offer', formData);
      setResult(response.data);
      setStep('result');
      
      // Save offer data to persistent workflow store
      setOfferData({
        offerName: response.data.offer_name,
        offerDescription: response.data.offer_description,
        offerTagline: response.data.offer_tagline,
        offerType: formData.offer_type,
        audience: response.data.target_audience,
        audiencePainPoint: formData.audience_pain_point,
        price: response.data.price_suggestion,
        benefits: response.data.key_benefits || [],
        bonuses: response.data.bonuses || [],
        urgency: response.data.urgency_element
      });
      
      if (onSuccess) onSuccess();
      toast.success('Your offer is ready!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to build offer');
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleClose = () => {
    setStep('input');
    setResult(null);
    setFormData({
      audience_pain_point: '',
      solution_description: '',
      offer_type: 'digital_product',
      price_range: 'mid'
    });
    onClose();
  };

  const handleNewOffer = () => {
    setStep('input');
    setResult(null);
  };

  // ──────────────────────────────────────────────────────────
  // Universal Editable Preview Mode (Phase 2) — Offer
  //
  // Edits PATCH to /api/monetization/offer/{id}. Server-side
  // whitelist (OFFER_EDITABLE_FIELDS) drops any non-editable
  // keys. business_memory is NEVER touched here.
  // ──────────────────────────────────────────────────────────
  const [savingOfferEdits, setSavingOfferEdits] = useState(false);
  const editableOffer = useMemo(() => ({
    offer_name: result?.offer_name || '',
    offer_tagline: result?.offer_tagline || '',
    offer_description: result?.offer_description || '',
    key_benefits: result?.key_benefits || [],
    price_suggestion: result?.price_suggestion || '',
    bonuses: result?.bonuses || [],
    urgency_element: result?.urgency_element || '',
    guarantee: result?.guarantee || '',
  }), [result]);

  const handleSaveOfferEdits = useCallback(async (next) => {
    if (!result?.id) {
      toast.error('Offer ID missing — please regenerate the offer.');
      return;
    }
    setSavingOfferEdits(true);
    try {
      const body = {
        offer_name: next.offer_name,
        offer_tagline: next.offer_tagline,
        offer_description: next.offer_description,
        key_benefits: next.key_benefits,
        price_suggestion: next.price_suggestion,
        bonuses: next.bonuses,
        urgency_element: next.urgency_element,
        guarantee: next.guarantee,
      };
      const { data } = await api.patch(`/monetization/offer/${result.id}`, body, { timeout: 30000 });
      setResult((prev) => (prev ? { ...prev, ...body, last_edited_at: data?.saved_at } : prev));
    } finally {
      setSavingOfferEdits(false);
    }
  }, [result?.id]);

  const handleCreateSalesPage = () => {
    if (onAction) {
      onAction('sales-page');
    }
    handleClose();
  };

  const handleWhatsNextAction = (action) => {
    if (onAction) {
      onAction(action);
    }
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center">
                <Package size={20} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Let's Build Your Offer</h2>
                <p className="text-sm text-slate-400">I'll help you create something sellable</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Input Step */}
            {step === 'input' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    What problem does your audience struggle with? *
                  </label>
                  <textarea
                    value={formData.audience_pain_point}
                    onChange={(e) => setFormData({...formData, audience_pain_point: e.target.value})}
                    data-testid="offer-pain-point"
                    placeholder="e.g., People want to make money online but don't know where to start..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    How can you help them solve it? *
                  </label>
                  <textarea
                    value={formData.solution_description}
                    onChange={(e) => setFormData({...formData, solution_description: e.target.value})}
                    data-testid="offer-solution"
                    placeholder="e.g., I teach beginners how to start a faceless YouTube channel..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Offer Type
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="offer-type-group">
                    {offerTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        data-testid={`offer-type-${type.id}`}
                        aria-pressed={formData.offer_type === type.id}
                        onClick={() => setFormData({...formData, offer_type: type.id})}
                        className={`p-3 rounded-lg text-left transition-all ${
                          formData.offer_type === type.id
                            ? 'bg-amber-500/20 border-amber-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`text-sm font-medium ${formData.offer_type === type.id ? 'text-amber-400' : 'text-white'}`}>
                          {type.label}
                        </span>
                        <p className="text-xs text-slate-500 mt-1">{type.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Price Range
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {priceRanges.map((range) => (
                      <button
                        key={range.id}
                        onClick={() => setFormData({...formData, price_range: range.id})}
                        className={`p-3 rounded-lg text-center transition-all ${
                          formData.price_range === range.id
                            ? 'bg-amber-500/20 border-amber-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`text-sm font-medium ${formData.price_range === range.id ? 'text-amber-400' : 'text-white'}`}>
                          {range.label}
                        </span>
                        <p className="text-xs text-slate-500">{range.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  data-testid="build-offer-submit"
                  className="w-full py-4 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all"
                >
                  <Sparkle size={20} weight="fill" />
                  <span>Create My Offer</span>
                </button>
              </div>
            )}

            {/* Generating Step */}
            {step === 'generating' && (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Spinner size={32} className="text-white animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">I'm Crafting Your Offer</h3>
                <p className="text-slate-400">Creating something your audience will love...</p>
              </div>
            )}

            {/* Result Step */}
            {step === 'result' && result && (
              <div className="space-y-6">
                <div className="p-4 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-center">
                  <Package size={32} className="text-amber-400 mx-auto mb-2" />
                  <p className="text-amber-400 font-medium">Here's your offer, ready to sell!</p>
                </div>

                {/* Round 92 — Why This Fits You + feedback */}
                <WhyThisFitsYouBlock
                  department="offer"
                  taskType="offer"
                  recommendationId={result?.id || null}
                  recommendationSummary={result?.offer_name ? `the "${result.offer_name}" offer` : null}
                  businessCtx={{
                    primary_offer: result?.offer_name,
                    target_audience: result?.target_audience,
                  }}
                />

                {/* Universal Editable Preview Mode — fix wording / price /
                    bonuses / urgency before downloading. Save persists to
                    /api/monetization/offer/{id}; business_memory never
                    changes. */}
                {result.id && (
                  <EditablePreview
                    draftKey={`offer-draft-${result.id}`}
                    originalValue={editableOffer}
                    onSave={handleSaveOfferEdits}
                    busy={savingOfferEdits}
                    savedAt={result.last_edited_at || null}
                    previewSlot={({ value }) => (
                      <div className="space-y-3 max-h-[360px] overflow-y-auto text-slate-200 text-sm">
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Offer name</p>
                          <p className="text-white text-base font-semibold">{value.offer_name || '—'}</p>
                        </div>
                        {value.offer_tagline && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Tagline</p>
                            <p className="text-amber-300">{value.offer_tagline}</p>
                          </div>
                        )}
                        {value.offer_description && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Description</p>
                            <p>{value.offer_description}</p>
                          </div>
                        )}
                        {value.price_suggestion && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Price</p>
                            <p className="text-emerald-300 font-medium">{value.price_suggestion}</p>
                          </div>
                        )}
                        {(value.key_benefits || []).length > 0 && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Benefits</p>
                            <ul className="list-disc list-inside space-y-1">
                              {(value.key_benefits || []).slice(0, 6).map((b, i) => (
                                <li key={`pv-b-${i}`}>{b}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {value.urgency_element && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Urgency</p>
                            <p className="text-rose-300">{value.urgency_element}</p>
                          </div>
                        )}
                      </div>
                    )}
                    editSlot={({ value, setValue }) => (
                      <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Offer name</span>
                          <input
                            type="text"
                            value={value.offer_name || ''}
                            onChange={(e) => setValue({ ...value, offer_name: e.target.value })}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none"
                            data-testid="offer-edit-name"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Tagline</span>
                          <input
                            type="text"
                            value={value.offer_tagline || ''}
                            onChange={(e) => setValue({ ...value, offer_tagline: e.target.value })}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none"
                            data-testid="offer-edit-tagline"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Description</span>
                          <textarea
                            value={value.offer_description || ''}
                            onChange={(e) => setValue({ ...value, offer_description: e.target.value })}
                            rows={3}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none"
                            data-testid="offer-edit-description"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Price</span>
                          <input
                            type="text"
                            value={value.price_suggestion || ''}
                            onChange={(e) => setValue({ ...value, price_suggestion: e.target.value })}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none"
                            data-testid="offer-edit-price"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Urgency</span>
                          <input
                            type="text"
                            value={value.urgency_element || ''}
                            onChange={(e) => setValue({ ...value, urgency_element: e.target.value })}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none"
                            data-testid="offer-edit-urgency"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Guarantee</span>
                          <textarea
                            value={value.guarantee || ''}
                            onChange={(e) => setValue({ ...value, guarantee: e.target.value })}
                            rows={2}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none"
                            data-testid="offer-edit-guarantee"
                          />
                        </label>
                        <div>
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Key benefits (one per line)</span>
                          <textarea
                            value={(value.key_benefits || []).join('\n')}
                            onChange={(e) => setValue({ ...value, key_benefits: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
                            rows={4}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none"
                            data-testid="offer-edit-benefits"
                          />
                        </div>
                        <div>
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Bonuses (one per line)</span>
                          <textarea
                            value={(value.bonuses || []).map(b => (typeof b === 'string' ? b : (b?.text || JSON.stringify(b)))).join('\n')}
                            onChange={(e) => setValue({ ...value, bonuses: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
                            rows={3}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none"
                            data-testid="offer-edit-bonuses"
                          />
                        </div>
                      </div>
                    )}
                  />
                )}

                {/* Offer Name */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Offer Name</label>
                    <button
                      onClick={() => handleCopy(result.offer_name, 'name')}
                      className="text-amber-400 hover:text-amber-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'name' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <p className="text-white text-xl font-bold" data-testid="offer-result-name">{result.offer_name}</p>
                </div>

                {/* Tagline */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Tagline</label>
                    <button
                      onClick={() => handleCopy(result.offer_tagline, 'tagline')}
                      className="text-amber-400 hover:text-amber-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'tagline' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <p className="text-amber-400 font-medium bg-amber-500/10 p-3 rounded-lg">{result.offer_tagline}</p>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Description</label>
                    <button
                      onClick={() => handleCopy(result.offer_description, 'desc')}
                      className="text-amber-400 hover:text-amber-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'desc' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <p className="text-white bg-white/5 p-4 rounded-lg">{result.offer_description}</p>
                </div>

                {/* Benefits */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Key Benefits</label>
                  <ul className="space-y-2">
                    {result.key_benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start space-x-2 text-white bg-white/5 p-3 rounded-lg">
                        <CheckCircle size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Target Audience */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Target Audience</label>
                  <p className="text-slate-300 bg-white/5 p-3 rounded-lg">{result.target_audience}</p>
                </div>

                {/* Price Suggestion */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Suggested Price</label>
                  <p className="text-green-400 font-semibold bg-green-500/10 p-3 rounded-lg">{result.price_suggestion}</p>
                </div>

                {/* Bonuses */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Bonus Ideas</label>
                  <ul className="space-y-2">
                    {result.bonuses.map((bonus, i) => (
                      <li key={i} className="text-white bg-white/5 p-3 rounded-lg">
                        <span className="text-amber-400">Bonus {i + 1}:</span> {bonus}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Urgency */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Urgency Element</label>
                  <p className="text-red-400 bg-red-500/10 p-3 rounded-lg">{result.urgency_element}</p>
                </div>

                {/* Success Message */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30">
                  <div className="flex items-center space-x-3">
                    <CheckCircle size={24} className="text-green-400" weight="fill" />
                    <div>
                      <p className="text-green-400 font-medium">Your offer is ready!</p>
                      <p className="text-slate-400 text-sm">Now let's set it up so you can start selling.</p>
                    </div>
                  </div>
                </div>

                {/* Finished Offer Preview (Growth = editable template, Pro = sell-ready product) */}
                {result.html_version && (
                  <>
                    <StyledHtmlPreview
                      html={result.html_version}
                      title={result.output_type === 'editable_template' ? 'Your Editable Offer Template' : 'Your Sell-Ready Offer'}
                      downloadName="Offer.html"
                    />
                    <button
                      onClick={async () => {
                        const blob = new Blob([result.html_version], { type: 'text/html' });
                        await safeDownloadBlob(blob, result.output_type === 'editable_template' ? 'Offer_Template.html' : 'Offer.html');
                      }}
                      data-testid="download-offer-html-btn"
                      className="w-full px-4 py-2.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 text-sm font-medium rounded-lg flex items-center justify-center space-x-2 transition-colors"
                    >
                      <ArrowDown size={14} />
                      <span>{result.output_type === 'editable_template' ? 'Download Editable HTML' : 'Download HTML'}</span>
                    </button>
                    {result.output_type === 'editable_template' && (
                      <p className="text-slate-500 text-xs">Edit the downloaded HTML to customize colors, text, images, and CTAs for your brand</p>
                    )}
                  </>
                )}

                {/* Output Actions - Copy, View PDF, Download PDF, Save */}
                <div className="pt-4 border-t border-white/10">
                  <OutputActions
                    content={`${result.offer_name}\n\n${result.offer_tagline}\n\n${result.offer_description}\n\nKey Benefits:\n${result.key_benefits.map(b => `• ${b}`).join('\n')}\n\nTarget Audience: ${result.target_audience}\n\nPrice: ${result.price_suggestion}`}
                    pdfType="offer"
                    pdfData={{
                      offer_name: result.offer_name,
                      offer_tagline: result.offer_tagline,
                      offer_description: result.offer_description,
                      key_benefits: result.key_benefits,
                      target_audience: result.target_audience,
                      price_suggestion: result.price_suggestion?.replace('$', ''),
                      simple_pitch: result.offer_tagline
                    }}
                    saveType="offer"
                    saveTitle={result.offer_name}
                    saveContent={result}
                  />
                </div>

                {/* Build Your Offer Section - Platform Recommendations */}
                <div className="pt-6 border-t border-white/10">
                  <BuildYourOffer
                    offerType={formData.offer_type}
                    offerName={result.offer_name}
                    onOpenPlatform={() => {
                      // Track platform open if needed
                    }}
                    onCreateSalesPage={handleCreateSalesPage}
                    onBackToDashboard={handleClose}
                  />
                </div>

                {/* Secondary Actions */}
                <div className="pt-4 border-t border-white/10">
                  <button
                    onClick={handleNewOffer}
                    data-testid="build-another-offer"
                    className="w-full py-3 bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white font-medium rounded-xl transition-colors text-sm"
                  >
                    Create Another Offer
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BuildOfferModal;
